/* eslint-disable camelcase */
import { InputField, TextAreaField, SelectV2Field } from 'lib/iterable-hook-form';
import { useEffect, useState } from 'react';
import JSONTree from 'core/jsonTree';
import { FormField } from 'ui/formField';
import { useFormContext, useFormState } from 'react-hook-form';
import { BUTTON_SIZES } from 'ui/button/constants';
import { ButtonGroup } from 'ui/buttonGroupV2/ButtonGroup';
import { BUTTON_GROUP_TYPES } from 'ui/buttonGroupV2/types';
import type { EventUploaderFormProviderType } from '../contexts/EventUploaderFormProvider';
import { ENDPOINTS_DROPDOWN_OPTIONS, ENDPOINT_BLUEPRINTS } from '../constants';

export const EventUploaderForm = ({ formId, onSubmit }: any) => {
  const [jsonSrcFieldType, setJsonSrcFieldType] = useState<'textbox' | 'src'>('textbox');
  const { handleSubmit, watch, setValue } = useFormContext<EventUploaderFormProviderType>();
  const { errors } = useFormState<EventUploaderFormProviderType>();

  const onJsonUpdate = ({ updated_src }: any) => {
    setValue('JSONsrc', JSON.stringify(updated_src, null, 2));
  };

  // the first input field -- the endpoint dropdown -- affects the default
  // value of the second input field, the JSONsrc field, so that the user is presented
  // with some basic information they can immediately use to populate mock data for a given endpoint.
  // the following code is what makes that all possible.
  const endpoint = watch('endpoint');
  useEffect(() => {
    if (endpoint) {
      setValue('JSONsrc', JSON.stringify(ENDPOINT_BLUEPRINTS[endpoint], null, 2));
    }
  }, [endpoint, setValue]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormField
        errorText={errors.endpoint?.message}
        hasError={!!errors.endpoint?.message}
        inputId="endpointFormFieldId"
        labelText="Endpoint"
      >
        <SelectV2Field
          autoFocus
          usePortal
          valueType="string"
          data-test="upload-event-endpoint-field"
          id="endpointFormFieldId"
          name="endpoint"
          options={ENDPOINTS_DROPDOWN_OPTIONS}
        />
      </FormField>
      <FormField
        restrictionText={
          <ButtonGroup
            type={BUTTON_GROUP_TYPES.SINGLE_SELECT}
            size={BUTTON_SIZES.SMALL}
            defaultSelectedIds={['textbox']}
            options={[
              {
                id: 'textbox',
                children: 'Use TextArea',
                onClick: (e) => {
                  // the prevent default is necessary because the button press is inside a form
                  // and for some reason causes the form to submit
                  e.preventDefault();
                  setJsonSrcFieldType('textbox');
                },
              },
              {
                id: 'json',
                children: 'Use JSON Editor',
                onClick: (e) => {
                  // the prevent default is necessary because the button press is inside a form
                  // and for some reason causes the form to submit
                  e.preventDefault();
                  setJsonSrcFieldType('src');
                },
              },
            ]}
          />
        }
        errorText={errors.JSONsrc?.message as string}
        hasError={!!errors.JSONsrc?.message}
        inputId="JSONsrcFormFieldId"
        labelText="Value"
      >
        {jsonSrcFieldType === 'textbox' ? (
          <TextAreaField
            rows={5}
            id="JSONsrcFormFieldId"
            name="JSONsrc"
            data-test="upload-event-JSONsrc-textArea-field"
          />
        ) : (
          <div data-test="upload-event-JSONsrc-tree-field">
            <JSONTree
              id="JSONsrcFormFieldId"
              src={watch('JSONsrc')}
              style={{ maxHeight: '45vh', overflowY: 'scroll' }}
              onAdd={onJsonUpdate}
              onDelete={onJsonUpdate}
              onEdit={onJsonUpdate}
            />
          </div>
        )}
      </FormField>
      <FormField
        errorText={errors.apiKey?.message}
        hasError={!!errors.apiKey?.message}
        inputId="apiKeyFormFieldId"
        labelText="API Key"
      >
        <InputField id="apiKeyFormFieldId" name="apiKey" data-test="upload-event-apiKey-field" />
      </FormField>
    </form>
  );
};
